import axios from "axios";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { defaultUrl } from "../features/actions";
import { MessageSquareText, Send, Sparkles, AlertCircle } from "lucide-react";
import { CircleLoader } from "./CircleLoader";

const Faq = () => {
  const token = sessionStorage.getItem("token");
  const [feedback, setFeedback] = useState("");
  const [isSending, setIsSending] = useState(false);

  const sendFeedback = async () => {
    if (feedback.trim().length < 4) {
      return toast.error("Feedback is too short. Please tell us a bit more!", {
        icon: <AlertCircle className="text-amber-500" size={18} />,
      });
    }

    setIsSending(true);
    const toastId = toast.loading("Sending your thoughts to our team...");

    try {
      const response = await axios.post(
        `${defaultUrl}/feedback`,
        { feedback },
        {
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );

      setFeedback("");
      toast.success(response.data.message || "Feedback sent! Thank you.", {
        id: toastId,
      });
    } catch (err: any) {
      toast.error("We couldn't send your feedback. Please try again.", {
        id: toastId,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="max-w-4xl animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header Section */}
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
            <MessageSquareText size={24} />
          </div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight">
            How can we <span className="text-blue-600">improve?</span>
          </h2>
        </div>
        <p className="text-slate-500 font-medium max-w-lg leading-relaxed">
          Your feedback directly shapes the future of this platform. Whether
          it's a bug, a feature request, or a general thought—we're listening.
        </p>
      </div>

      {/* Feedback Form */}
      <div className="relative">
        <div className="absolute -top-3 -right-3 p-3 bg-amber-100 text-amber-600 rounded-full animate-pulse">
          <Sparkles size={16} />
        </div>

        <div className="bg-slate-50 rounded-[2rem] border-2 border-transparent focus-within:border-blue-100 focus-within:bg-white transition-all p-2">
          <textarea
            onChange={(e) => setFeedback(e.target.value)}
            value={feedback}
            placeholder="Type your message here... be as detailed as you like."
            className="w-full min-h-[300px] p-6 bg-transparent outline-none text-slate-700 font-bold text-lg placeholder:text-slate-300 placeholder:font-medium resize-none"
          />

          <div className="flex items-center justify-between p-4 bg-white rounded-[1.5rem] shadow-sm">
            <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full">
              <div
                className={`w-2 h-2 rounded-full ${feedback.length > 4 ? "bg-green-500" : "bg-slate-300"}`}
              />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                {feedback.length} Characters
              </span>
            </div>

            <button
              onClick={sendFeedback}
              disabled={isSending || feedback.length < 4}
              className={`flex items-center gap-3 px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all
                ${
                  isSending || feedback.length < 4
                    ? "bg-slate-100 text-slate-300 cursor-not-allowed"
                    : "bg-blue-600 text-white shadow-xl shadow-blue-100 hover:bg-blue-700 hover:-translate-y-0.5 active:translate-y-0"
                }`}
            >
              {isSending ? (
                <>
                  <CircleLoader /> Sending...
                </>
              ) : (
                <>
                  <Send size={16} /> Send Feedback
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* FAQ Quick Links (Optional Placeholder) */}
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-6 bg-white border border-slate-100 rounded-3xl hover:border-blue-200 transition-colors cursor-pointer group">
          <h4 className="font-black text-slate-800 text-sm mb-1 group-hover:text-blue-600 transition-colors">
            Documentation
          </h4>
          <p className="text-xs text-slate-400 font-medium">
            Learn how to use every feature like a pro.
          </p>
        </div>
        <div className="p-6 bg-white border border-slate-100 rounded-3xl hover:border-blue-200 transition-colors cursor-pointer group">
          <h4 className="font-black text-slate-800 text-sm mb-1 group-hover:text-blue-600 transition-colors">
            Video Tutorials
          </h4>
          <p className="text-xs text-slate-400 font-medium">
            Watch step-by-step guides for administrators.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Faq;
